/**
 * RulesSnapshotPanel
 * Expandable panel showing the frozen rule configuration from an import run.
 */

import React, { useState } from 'react';
import { ChevronDown, Shield, ShieldOff } from 'lucide-react';

const RulesSnapshotPanel = ({ rulesSnapshot }) => {
  const [isOpen, setIsOpen] = useState(false);

  if (!rulesSnapshot || !rulesSnapshot.rules) return null;

  const rules = rulesSnapshot.rules;
  const version = rulesSnapshot.rules_version;

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-slate-400" strokeWidth={1.5} />
          <span className="text-sm font-medium text-slate-700">
            Rules Snapshot
          </span>
          {version !== undefined && (
            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-500">
              v{version}
            </span>
          )}
        </div>
        <ChevronDown
          className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          strokeWidth={1.5}
        />
      </button>

      {isOpen && (
        <div className="border-t border-slate-200 px-4 py-3 space-y-2 bg-slate-50/50">
          {rules.length === 0 ? (
            <p className="text-xs text-slate-400 italic">No rules captured</p>
          ) : (
            rules.map((rule) => (
              <div
                key={rule.rule_code}
                className="flex items-start gap-3 py-2 border-b border-slate-100 last:border-b-0"
              >
                {rule.enabled !== false ? (
                  <Shield className="h-3.5 w-3.5 text-emerald-500 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                ) : (
                  <ShieldOff className="h-3.5 w-3.5 text-slate-300 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-slate-700">
                      {rule.name || rule.rule_code}
                    </span>
                    {rule.version !== undefined && (
                      <span className="text-[10px] text-slate-400">
                        v{rule.version}
                      </span>
                    )}
                    {rule.enabled === false && (
                      <span className="text-[10px] text-slate-400 italic">disabled</span>
                    )}
                  </div>
                  {rule.config && Object.keys(rule.config).length > 0 && (
                    <p className="text-[11px] text-slate-400 mt-0.5 truncate">
                      {Object.entries(rule.config)
                        .filter(([, v]) => !Array.isArray(v))
                        .map(([k, v]) => `${k}: ${v}`)
                        .join(', ')}
                    </p>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default RulesSnapshotPanel;
